
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Product, ProductComponent, Items, Book } from '@/api/entities';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Search, ShoppingCart, Package, ChevronDown, ChevronRight, Plus, Edit, Trash2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ProductForm from './ProductForm';
import { Button } from '@/components/ui/button';
import { useLocalization } from '@/components/providers/LocalizationProvider';

export default function ProductsView() {
    const { t } = useTranslation();
    const { currencySymbol } = useLocalization();
    const [products, setProducts] = useState([]);
    const [productComponents, setProductComponents] = useState([]);
    const [bookEditions, setBookEditions] = useState([]);
    const [simpleProducts, setSimpleProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [expandedProducts, setExpandedProducts] = useState(new Set());
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [componentNames, setComponentNames] = useState({});
    const [creatingBookId, setCreatingBookId] = useState(null);

    const getProductTypeLabel = (type) => {
        if (type === 'simple_product') return t('product.simple_product');
        if (type === 'book_edition') return t('product.book_edition');
        if (type === 'bundle') return t('product.bundle');
        return '-';
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        filterProducts();
    }, [products, searchTerm]);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [productsData, componentsData, editionsData, simpleProductsData] = await Promise.all([
                Product.list(),
                ProductComponent.list(),
                Items.list({ type: 'book_edition' }),
                Items.list({ type: 'simple_product' })
            ]);
            
            setProducts(productsData);
            setProductComponents(componentsData);
            setBookEditions(editionsData);
            setSimpleProducts(simpleProductsData);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
        setIsLoading(false);
    };

    const filterProducts = () => {
        let filtered = products;
        if (searchTerm) {
            filtered = filtered.filter(product =>
                product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        setFilteredProducts(filtered);
    };

    const toggleProductExpansion = (productId) => {
        const newExpanded = new Set(expandedProducts);
        if (newExpanded.has(productId)) {
            newExpanded.delete(productId);
        } else {
            newExpanded.add(productId);
        }
        setExpandedProducts(newExpanded);
    };

    const handleProductSave = async (savedProduct) => {
        try {
            // Update local state with the saved/updated product without extra API calls
            if (savedProduct && savedProduct.id) {
                setProducts(prev => {
                    const existingIndex = prev.findIndex(p => p.id === savedProduct.id);
                    if (existingIndex !== -1) {
                        const updated = [...prev];
                        updated[existingIndex] = savedProduct;
                        return updated;
                    }
                    return [savedProduct, ...prev];
                });
            } else {
                // Fallback: refetch if payload is missing id (shouldn't happen)
                fetchData();
            }
            setIsProductModalOpen(false);
            setSelectedProduct(null);
        } catch (error) {
            console.error("Error saving product:", error);
        }
    };

    const handleEditProduct = (product) => {
        setSelectedProduct(product);
        setIsProductModalOpen(true);
    };

    const handleDeleteProduct = async (product) => {
        if (window.confirm(`Are you sure you want to delete "${product.name}"?`)) {
            try {
                await Product.delete(product.id);
                fetchData();
            } catch (error) {
                console.error("Error deleting product:", error);
            }
        }
    };

    const getComponentDisplayName = (component) => {
        // Новая унифицированная логика: используем реальные ключи компонент
        const editionId = component.book_edition_id || component.item_id; // поддержка старого поля
        if (editionId) {
            const edition = bookEditions.find(e => e.id === editionId);
            if (edition) {
                return `${edition.name || 'Unknown Edition'} (x${component.quantity})`;
            }
        }
        const productId = component.single_product_id || component.product_id; // поддержка старого поля
        if (productId) {
            const product = simpleProducts.find(p => p.id === productId);
            if (product) {
                return `${product.name || 'Unknown Product'} (x${component.quantity})`;
            }
        }
        return `Component (x${component.quantity})`;
    };

    const handleCreateProduct = () => {
        setSelectedProduct(null);
        setIsProductModalOpen(true);
    };

    const handleCreateBookForItem = async (product) => {
        try {
            setCreatingBookId(product.id);
            const title = product.name;
            let author = product.metadata?.author;
            if (!author) {
                author = window.prompt('Enter author for the book');
                if (!author) {
                    return;
                }
            }

            const pages = product.pages || product.metadata?.pages;
            const description = product.description || '';

            const createdBook = await Book.create({ title, author, description, pages });

            const newMetadata = { ...(product.metadata || {}), book_id: createdBook.id };
            await Items.update(product.id, { metadata: newMetadata });

            await fetchData();
        } catch (e) {
            console.error('Failed to create book and link to item:', e);
        } finally {
            setCreatingBookId(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-slate-800">Products</h2>
                <Button onClick={handleCreateProduct} className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                </Button>
            </div>

            <Card className="elegant-card">
                <CardContent className="p-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <Input
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 border-slate-200 focus:border-amber-500 focus:ring-amber-500"
                        />
                    </div>
                </CardContent>
            </Card>

            <Card className="elegant-card">
              <CardContent className="p-0">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-bold text-slate-900 w-8"></TableHead>
                      <TableHead className="font-bold text-slate-900">Name</TableHead>
                      <TableHead className="font-bold text-slate-900">SKU</TableHead>
                      <TableHead className="font-bold text-slate-900">Type</TableHead>
                      <TableHead className="font-bold text-slate-900">Price</TableHead>
                      <TableHead className="font-bold text-slate-900">Composition</TableHead>
                      <TableHead className="font-bold text-slate-900">Status</TableHead>
                      <TableHead className="font-bold text-slate-900">Allowed for sale</TableHead>
                      <TableHead className="font-bold text-slate-900 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredProducts.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                                <div className="flex flex-col items-center gap-2">
                                    <ShoppingCart className="w-12 h-12 text-slate-300" />
                                    <p className="font-medium">No products found</p>
                                    <p className="text-sm">Create your first product to get started</p>
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        filteredProducts.map(product => {
                            const productComponentsForProduct = productComponents.filter(c => c.product_id === product.id);
                            const isExpanded = expandedProducts.has(product.id);
                            const hasComponents = productComponentsForProduct.length > 0;
                            
                            return (
                                <React.Fragment key={product.id}>
                                    <TableRow className="hover:bg-slate-50/50">
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => toggleProductExpansion(product.id)}
                                                className="h-6 w-6 p-0"
                                            >
                                                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                            </Button>
                                        </TableCell>
                                        <TableCell className="font-medium text-slate-900">{product.name}</TableCell>
                                        <TableCell className="font-mono text-sm text-slate-500">{product.sku}</TableCell>
                                        <TableCell>
                                            <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                                                {getProductTypeLabel(product.type)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="font-medium text-slate-900">
                                            {product.price ? `${currencySymbol}${product.price}` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            {hasComponents ? (
                                                <div className="space-y-1">
                                                    {productComponentsForProduct.slice(0, 2).map((component, index) => (
                                                        <div key={index} className="flex items-center gap-1">
                                                            <Package className="w-3 h-3 text-slate-400" />
                                                            <span className="text-sm text-slate-600">
                                                                {getComponentDisplayName(component)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    {productComponentsForProduct.length > 2 && (
                                                        <p className="text-xs text-slate-500">
                                                            and {productComponentsForProduct.length - 2} more...
                                                        </p>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-slate-400">Empty product</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {product.is_active ? (
                                                <Badge className="bg-green-600 text-white border-green-700">Active</Badge>
                                            ) : (
                                                <Badge className="bg-gray-600 text-white border-gray-700">Inactive</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {product.is_allowed_for_sale ? (
                                                <Badge className="bg-green-100 text-green-800 border-green-200">Yes</Badge>
                                            ) : (
                                                <Badge className="bg-gray-100 text-gray-800 border-gray-200">No</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {product.type === 'book_edition' && !(product.metadata && product.metadata.book_id) && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => handleCreateBookForItem(product)}
                                                        disabled={creatingBookId === product.id}
                                                    >
                                                        {creatingBookId === product.id ? 'Creating…' : 'Create book'}
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="icon" onClick={() => handleEditProduct(product)}>
                                                    <Edit className="w-4 h-4" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    onClick={() => handleDeleteProduct(product)}
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    {isExpanded && hasComponents && (
                                        <TableRow>
                                            <TableCell colSpan={8} className="bg-slate-50/50 p-4">
                                                <div className="space-y-3">
                                                    <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                                                        <Package className="w-4 h-4" />
                                                        Components
                                                    </h4>
                                                    <div className="space-y-2">
                                                        {productComponentsForProduct.map((component, index) => (
                                                            <div key={index} className="bg-white rounded-lg p-3 border border-slate-200">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-sm text-slate-600">
                                                                        {getComponentDisplayName(component)}
                                                                    </span>
                                                                    <Badge className="bg-blue-100 text-blue-800">
                                                                        Type: {(component.book_edition_id || component.item_id) ? 'Book Edition' : 'Simple Product'}
                                                                    </Badge>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            );
                        })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <ProductForm
                isOpen={isProductModalOpen}
                onClose={() => setIsProductModalOpen(false)}
                product={selectedProduct}
                productComponents={productComponents}
                onSave={handleProductSave}
            />
        </div>
    );
}
